import { useState } from "react";
import { useApproveWalletEntry } from "../hooks/useWallets";
import type { WalletLedgerEntry } from "../services/walletsApi";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Field, FieldLabel } from "@/components/ui/field";
import { useNotification } from "@/shared/hooks/useNotification";

const MPESA_PATTERN = /^[A-Z0-9]{10}$/;

interface Props {
  entry: WalletLedgerEntry;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Cash-out approval modal — finance pastes the disbursement reference and
 * confirms the money has been sent before the wallet debit is recorded.
 * The confirm checkbox adds intentional friction so finance cannot approve
 * before disbursing (see SPEC_CUSTOMER_WALLET §Flow D).
 */
export const CashOutApprovalDialog = ({ entry, open, onOpenChange }: Props) => {
  const notification = useNotification();
  const mutation = useApproveWalletEntry();
  const [reference, setReference] = useState(entry.payoutReference ?? "");
  const [notes, setNotes] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const validateReference = () => {
    const ref = reference.trim();
    if (!ref) return "Reference is required.";
    if (entry.payoutChannel === "MPesa") {
      if (!MPESA_PATTERN.test(ref))
        return "Mpesa reference must be 10 uppercase alphanumeric characters.";
    } else if (ref.length < 4 || ref.length > 30) {
      return "Reference must be 4–30 characters.";
    }
    return null;
  };

  const submit = () => {
    const refError = validateReference();
    if (refError) {
      notification.error(refError);
      return;
    }
    if (!confirmed) {
      notification.error("Confirm the disbursement has been sent before approving.");
      return;
    }

    mutation.mutate(
      {
        entryId: entry.id,
        payload: {
          approvalNotes: notes.trim() || null,
          payoutReference: reference.trim(),
          disbursementConfirmed: true,
        },
      },
      {
        onSuccess: () => {
          notification.success("Cash-out approved.");
          onOpenChange(false);
        },
        onError: () => {
          notification.error("Approval failed — likely insufficient balance, please refresh.");
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Approve cash-out</DialogTitle>
          <DialogDescription>
            {entry.payoutChannel} — KES {entry.amount.toFixed(2)}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Field>
            <FieldLabel>
              Payout reference <span className="text-destructive">*</span>
            </FieldLabel>
            <Input
              placeholder={
                entry.payoutChannel === "MPesa" ? "Mpesa code (10 chars)" : "Reference 4–30 chars"
              }
              value={reference}
              onChange={(e) => setReference(e.target.value.toUpperCase())}
              disabled={mutation.isPending}
            />
          </Field>

          <Field>
            <FieldLabel>Approval notes</FieldLabel>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} disabled={mutation.isPending} />
          </Field>

          <label className="flex items-start gap-2 text-sm">
            <Checkbox
              checked={confirmed}
              onCheckedChange={(v) => setConfirmed(v === true)}
              disabled={mutation.isPending}
            />
            <span>
              I confirm the disbursement has been sent to the customer via {entry.payoutChannel}.
            </span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={mutation.isPending || !confirmed}>
            {mutation.isPending ? "Approving…" : "Approve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
